import { useEffect, useState } from 'react'

import { getAccessToken } from '../../../lib/api'

/** Load an access-controlled image into an object URL.
 *
 *  A plain <img src> cannot carry an Authorization header, so pointing it at a
 *  protected endpoint just 401s and renders an empty box. Fetching the bytes
 *  ourselves keeps the endpoint properly guarded instead of having to open it
 *  up or leak a token into the query string.
 *
 *  In production this is where a short-lived signed URL would go, so the image
 *  can be served straight from object storage without touching the API.
 */
export function useAuthedImage(url: string) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let revoked: string | null = null
    const controller = new AbortController()

    ;(async () => {
      setError(null)
      setObjectUrl(null)
      try {
        const token = getAccessToken()
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          signal: controller.signal,
        })
        if (!response.ok) {
          setError(
            response.status === 404
              ? 'The original file is no longer stored.'
              : `Could not load the document (${response.status}).`,
          )
          return
        }
        const blob = await response.blob()
        revoked = URL.createObjectURL(blob)
        setObjectUrl(revoked)
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          setError('Could not load the document.')
        }
      }
    })()

    return () => {
      controller.abort()
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [url])

  return { objectUrl, error }
}
